import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useTranslation, getSessionStorage } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import { EventPageVisibility } from '@graphql/generated-base';
import { useWebsitePages } from '@shared/hooks/useWebsitePages/useWebsitePages';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';
import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import { useHotelRoutePaths } from '@shared/utils/accommodations/hotelRoutes';
import type { SearchParams } from '@shared/utils/accommodations/hotelRoutes';
import { resolveAcquisitionAwareUtm, useUtmParameters } from '@shared/utils/utmParameters';
import { useMarriottRoomBlockBannerQuery } from './MarriottRoomBlockBanner.graphql.generated';
import { pickMarriottRoomBlock } from './marriottRoomBlockBanner.utils';

const DISMISSED_KEY = 'guest.marriottRoomBlockBannerDismissed';

const BANNER_UTM: SearchParams = { utm_medium: 'web', utm_source: 'room_block_banner', utm_campaign: 'marriott_room_block_banner' };

type UseMarriottRoomBlockBannerArgs = Readonly<{
  eventId?: string;
  eventHandle: string;
  /** True for host preview, native webview and full-page password/OTP gates: no ad there. */
  isSuppressed: boolean;
}>;

/** Data for the Marriott room block strip, gated by the `marriottRoomBlockBanner` experiment. */
export const useMarriottRoomBlockBanner = ({ eventId, eventHandle, isSuppressed: isSuppressedByCaller }: UseMarriottRoomBlockBannerArgs) => {
  const { tq } = useTranslation('accommodationsGuest');
  const history = useHistory();
  const telemetry = useGuestSiteTelemetry();
  const hotelRoutePaths = useHotelRoutePaths();
  const { formatDate } = useEventLocaleFormatter();

  // A hidden or password-protected Where to Stay page means the host hasn't published the hotel.
  const { getPageByName, isPageListLoaded } = useWebsitePages();
  const isAccommodationsPublic = getPageByName(EVENT_PAGE_SLUGS.Accommodations)?.visibility === EventPageVisibility.public;
  const isSuppressed = isSuppressedByCaller || !isAccommodationsPublic;

  // Derived during render so a dismissal never leaks across events, even for one render.
  const storageKey = eventId ? `${DISMISSED_KEY}.${eventId}` : undefined;
  const [dismissedKeys, setDismissedKeys] = useState<ReadonlySet<string>>(() => new Set());
  const isStoredDismissed = useMemo(() => (storageKey ? getSessionStorage().getItem(storageKey) === 'true' : false), [storageKey]);
  const dismissed = !!storageKey && (isStoredDismissed || dismissedKeys.has(storageKey));

  const isQuerySkipped = !eventId || isSuppressed || dismissed;
  const { data, loading: isQueryLoading } = useMarriottRoomBlockBannerQuery({
    variables: { eventId: eventId ?? '' },
    skip: isQuerySkipped,
    ssr: false,
    batchMode: 'fast',
    errorPolicy: 'all',
  });

  // A skipped query can keep its last result, so gate on the skip too.
  const target = isQuerySkipped ? undefined : pickMarriottRoomBlock(data?.event?.accommodationPlacesV2 ?? []);

  // Reading the flag logs the exposure, so read it only once the banner would show: both arms exposed alike.
  const flag = useFeatureValue('marriottRoomBlockBanner', { skip: !target });
  const isTreatment = flag.value === 'treatment';
  const weddingCodeFirst = (flag.payload as Readonly<{ weddingCodeFirst?: unknown }> | undefined)?.weddingCodeFirst === true;

  const isVisible = !!target && isTreatment;
  const isResolved = isPageListLoaded && (isQuerySkipped || ((!!data || !isQueryLoading) && (!target || !flag.loading)));
  const formattedCutoff = target ? (formatDate(target.cutoffDate, 'MMMddyyyy') ?? '') : '';

  const placeId = target?.placeId;
  const hotelName = target?.hotelName ?? '';
  const daysLeft = target?.daysLeft ?? 0;

  // Same moment as Amplitude's exposure, so the two line up.
  const flagValue = flag.value;
  const isFlagLoading = flag.loading;
  const exposedPlaceIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!placeId || isFlagLoading || exposedPlaceIdRef.current === placeId) {
      return;
    }
    exposedPlaceIdRef.current = placeId;
    telemetry.marriottRoomBlockBannerExposed({ placeId, hotelName, daysLeft }, flagValue, weddingCodeFirst);
  }, [placeId, isFlagLoading, flagValue, weddingCodeFirst, hotelName, daysLeft, telemetry]);

  const trackedPlaceIdRef = useRef<string | null>(null);
  const handleViewed = useCallback(() => {
    if (!placeId || trackedPlaceIdRef.current === placeId) {
      return;
    }
    trackedPlaceIdRef.current = placeId;
    telemetry.marriottRoomBlockBannerViewed({ placeId, hotelName, daysLeft });
  }, [placeId, hotelName, daysLeft, telemetry]);

  // The same merge the room block route applies to the URL (inbound UTM wins), so event and URL agree.
  const currentUtm = useUtmParameters();
  const clickUtm = useMemo(() => resolveAcquisitionAwareUtm(currentUtm, BANNER_UTM), [currentUtm]);

  const handleBook = useCallback(() => {
    if (!target) {
      return;
    }
    telemetry.marriottRoomBlockBannerClicked({ placeId: target.placeId, hotelName: target.hotelName, daysLeft: target.daysLeft }, clickUtm);
    history.push(hotelRoutePaths.roomblock.buildRelativeUrl(eventHandle, target.placeId, BANNER_UTM));
  }, [target, telemetry, clickUtm, history, hotelRoutePaths, eventHandle]);

  const handleClose = useCallback(() => {
    if (storageKey) {
      getSessionStorage().setItem(storageKey, 'true');
      setDismissedKeys(prev => new Set(prev).add(storageKey));
    }
    if (target) {
      telemetry.marriottRoomBlockBannerDismissed({ placeId: target.placeId, hotelName: target.hotelName, daysLeft: target.daysLeft });
    }
  }, [storageKey, target, telemetry]);

  const message = tq`The couple has arranged a room block for their guests at ${hotelName}. Please book by ${formattedCutoff}.`;

  return {
    isVisible,
    isResolved,
    weddingCodeFirst,
    placeId,
    message,
    actionLabel: tq`Book Now`,
    onBook: handleBook,
    onClose: handleClose,
    onViewed: handleViewed,
  };
};
